import { useCallback, useEffect, useRef, useState } from 'react'

import { createSSEConnection } from './client'
import { SSEConnection, SSEConnectionConfig, SSEConnectionState, SSE } from './types'

export interface UseSSEOptions<Event extends SSE = SSE> extends Omit<SSEConnectionConfig<Event>, 'onStateChange'> {}

export interface UseSSEResult {
  // Current connection state
  state: SSEConnectionState
  // Last error that occurred, if any
  error: Error | null
  // Manually close and reopen the connection
  reconnect: () => void
}

/**
 * React hook for managing SSE connections.
 *
 * @param options - SSE connection options, or null to skip connecting
 * @returns Connection state and control functions
 *
 * @example
 * ```tsx
 * const { state, error, reconnect } = useSSE({
 *   url: '/api/events',
 *   getAuthToken: () => authManager.getAccessToken(),
 *   onEvent: (event) => console.log('Received:', event),
 * })
 * ```
 */
export function useSSE<Event extends SSE = SSE>(options: UseSSEOptions<Event> | null): UseSSEResult {
  const [state, setState] = useState<SSEConnectionState>('disconnected')
  const [error, setError] = useState<Error | null>(null)

  const connectionRef = useRef<SSEConnection | null>(null)
  const optionsRef = useRef(options)

  // Keep options ref up to date
  optionsRef.current = options

  const connect = useCallback(() => {
    // Close existing connection
    if (connectionRef.current) {
      connectionRef.current.close()
      connectionRef.current = null
    }

    const currentOptions = optionsRef.current
    if (!currentOptions) {
      setState('disconnected')
      return
    }

    setError(null)

    connectionRef.current = createSSEConnection<Event>({
      url: currentOptions.url,
      getAuthToken: currentOptions.getAuthToken,
      getOrganizationId: currentOptions.getOrganizationId,
      reconnect: currentOptions.reconnect,
      // Wrap callbacks to always use latest versions from ref
      onEvent: event => {
        optionsRef.current?.onEvent(event)
      },
      onStateChange: newState => {
        setState(newState)
      },
      onError: err => {
        setError(err)
        optionsRef.current?.onError?.(err)
      },
      onDisconnected: () => {
        optionsRef.current?.onDisconnected?.()
      },
    })
  }, [])

  const reconnect = useCallback(() => {
    connect()
  }, [connect])

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (options) {
      connect()
    }

    return () => {
      if (connectionRef.current) {
        connectionRef.current.close()
        connectionRef.current = null
      }
    }
    // Callbacks aren't included in the deps; they're accessed via stable refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.url])

  // Handle options becoming null (disconnect)
  useEffect(() => {
    if (!options && connectionRef.current) {
      connectionRef.current.close()
      connectionRef.current = null
      setState('disconnected')
    }
  }, [options])

  return {
    state,
    error,
    reconnect,
  }
}
